import { GIFEncoder, quantize, applyPalette } from "gifenc";
import floydSteinberg from "./dither";

interface Options {
  startTime?: number;
  endTime?: number;
  aspectRatio?: number;
  frameRate?: number;
  dither?: boolean;
  height?: number;
}

export enum EncoderEvents {
  Progress = "progress",
  Completed = "completed",
  Cancelled = "cancelled"
}

// https://support.giphy.com/hc/en-us/articles/360019914771-GIF-Creation-Best-Practices#:~:text=Follow%20our%20best%20practices%20for,you%20keep%20it%20at%20480p.
const DEFAULT_FRAME_RATE = 10;
const DEFAULT_OUTPUT_HEIGHT = 360;

type EventHandler = (...args: any) => unknown;

type DrawDimensions = [number, number, number, number];

// Safari < 16.4 doesn't support OffscreenCanvas so we need to fallback to a regular canvas
const createCanvas = (width: number, height: number): OffscreenCanvas | HTMLCanvasElement => {
  if (window.OffscreenCanvas) {
    return new OffscreenCanvas(width, height);
  } else {
    const canvas = document.createElement("canvas");

    Object.assign(canvas, {
      width,
      height
    });

    return canvas;
  }
};

export default class VideoToGIFEncoder {
  private observers = new Map<EncoderEvents, Set<EventHandler>>([]);

  private canvas = createCanvas(0, 0);
  private ctx = this.canvas.getContext("2d") as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
  private video = document.createElement("video");

  private gif = GIFEncoder();
  private canvasWidth = 0;
  private canvasHeight = 0;
  private cancelled = false;
  private drawDimensions?: DrawDimensions;
  private dither = floydSteinberg;

  get outputHeight() {
    return this.options.height ?? DEFAULT_OUTPUT_HEIGHT;
  }

  get frameRate() {
    return this.options.frameRate ?? DEFAULT_FRAME_RATE;
  }

  get videoWidth(): number {
    return this.video.videoWidth;
  }

  get videoHeight(): number {
    return this.video.videoHeight;
  }

  get intrinsicAspectRatio(): number {
    return this.videoWidth / this.videoHeight;
  }

  get aspectRatio(): number {
    return this.options.aspectRatio ?? this.intrinsicAspectRatio;
  }

  get duration(): number {
    return this.endTime - this.startTime;
  }

  get startTime(): number {
    return this.options.startTime ?? 0;
  }

  get endTime(): number {
    return this.options.endTime ?? this.video?.duration ?? 0;
  }

  constructor(private readonly url: string, private readonly options: Options) {}

  public async encode(): Promise<Uint8Array | undefined> {
    const encodeStartTime = new Date().getTime();

    await this.loadVideo();
    this.setCanvasDimensions();

    // Check that video is ready to play
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
    if (this.video.readyState !== 4) {
      throw Error("Video is not ready to encode");
    }

    const frameDelay = 1 / this.frameRate;
    const startTime = this.startTime;
    const duration = this.duration;

    let time = 0;

    while (time <= duration) {
      if (this.cancelled) {
        this.gif.finish();
        this.trigger(EncoderEvents.Cancelled);
        return undefined;
      }

      await this.encodeFrame(startTime + time);

      this.trigger(EncoderEvents.Progress, time / duration);
      time += frameDelay;
    }

    this.gif.finish();
    const bytes = this.gif.bytes();

    const timeToEncode = new Date().getTime() - encodeStartTime;

    this.trigger(EncoderEvents.Completed, bytes, timeToEncode);

    return bytes;
  }

  public cancel(): void {
    this.cancelled = true;
  }

  public subscribe(eventName: EncoderEvents, handler: EventHandler): void {
    let listeners = this.observers.get(eventName);

    if (!listeners) {
      listeners = new Set();
      this.observers.set(eventName, listeners);
    }

    listeners.add(handler);
  }

  public unsubscribe(eventName: EncoderEvents, handler: EventHandler): void {
    const listeners = this.observers.get(eventName);

    if (listeners) {
      listeners.delete(handler);
    }
  }

  private getDrawDimensions(): DrawDimensions {
    const relativeRatio = this.intrinsicAspectRatio / this.aspectRatio;

    let x = 0;
    let y = 0;
    let w = this.canvasWidth;
    let h = this.canvasHeight;

    if (relativeRatio > 1) {
      w = relativeRatio * this.canvasWidth;
      x = (this.canvasWidth - w) / 2;
    }

    if (relativeRatio < 1) {
      h = (1 / relativeRatio) * this.canvasHeight;
      y = (this.canvasHeight - h) / 2;
    }

    return [x, y, w, h];
  }

  private trigger(eventName: EncoderEvents, ...args: any[]): void {
    const listeners = this.observers.get(eventName);

    if (listeners) {
      listeners.forEach((listener) => {
        listener(...args);
      });
    }
  }

  private loadVideo(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.video.addEventListener("canplay", () => resolve());

      this.video.muted = true;
      this.video.src = this.url;
    });
  }

  private setCanvasDimensions(): void {
    this.canvasHeight = Math.min(this.outputHeight, this.videoHeight);
    this.canvasWidth = Math.round(this.canvasHeight * this.aspectRatio);

    this.canvas = createCanvas(this.canvasWidth, this.canvasHeight);
    this.ctx = this.canvas.getContext("2d", { willReadFrequently: true }) as OffscreenCanvasRenderingContext2D;
  }

  private async encodeFrame(seconds: number): Promise<void> {
    await this.seekVideoToTime(seconds);
    await this.drawVideoFrameToCanvas();
    const imageData = this.getCanvasImageData();
    this.encodeImageData(imageData);
  }

  private seekVideoToTime(seconds: number): Promise<void> {
    this.video.pause();

    return new Promise<void>((resolve) => {
      const onTimeUpdate = (ev: Event) => {
        if (ev.target instanceof HTMLVideoElement) {
          ev.target.removeEventListener("timeupdate", onTimeUpdate);
        }
        resolve();
      };

      this.video.addEventListener("timeupdate", onTimeUpdate);
      this.video.currentTime = seconds;
    });
  }

  private getCanvasImageData(): ImageData {
    return this.ctx.getImageData(0, 0, this.canvasWidth, this.canvasHeight);
  }

  private drawVideoFrameToCanvas(): Promise<void> {
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        const [x, y, w, h] = (this.drawDimensions ??= this.getDrawDimensions());

        this.ctx.drawImage(this.video, x, y, w, h);
        resolve();
      });
    });
  }

  private encodeImageData(imageData: ImageData): void {
    const { data } = imageData;

    const palette = quantize(imageData.data, 256, {
      format: "rgb444"
    });

    const pixels = this.options.dither ? this.dither(data, this.canvasWidth, this.canvasHeight, palette) : data;

    const index = applyPalette(pixels, palette);
    const delay = (1 / this.frameRate) * 1000;

    this.gif.writeFrame(index, this.canvasWidth, this.canvasHeight, {
      palette,
      delay
    });
  }
}
